import type { SchedulePeriod } from '@reducers/page-filters/page-filters.model';

/** Weekday flags as stored on a roster (`mo`…`su`). */
export type Weekday = 'mo' | 'tu' | 'we' | 'th' | 'fr' | 'sa' | 'su';

export const WEEKDAYS: Weekday[] = ['mo', 'tu', 'we', 'th', 'fr', 'sa', 'su'];

/**
 * A recommendation entry after it has been matched against the teams, shift
 * templates and employees that were actually created during onboarding. Only
 * entries whose team AND shift template resolved survive; an unresolved employee
 * becomes an open shift (`userId === ''`).
 */
export interface ReconciledEntry {
  teamId: string;
  shiftId: string;
  departmentId: string;
  /** Empty string = open (unassigned) shift. */
  userId: string;
  startTime: string;
  endTime: string;
  days: Weekday[];
  // Display-only, for the accept panel.
  teamName: string;
  shiftName: string;
  employeeName: string | null;
}

/** Minimal store slices the reconciler needs — kept as plain data so it stays pure/testable. */
export interface ReconciliationSource {
  teams: { id?: string; name: string; department_id: string }[];
  // Shift templates carry a short `name` ("Wor") and a descriptive `long_name`
  // ("Work @ Home"); the recommendation and the grid both use long_name.
  shifts: { id: string; name: string; long_name: string; department_id: string; starttime: string; endtime: string }[];
  employees: { id?: string; name: string; first_name: string; last_name: string }[];
}

export interface DraftScheduleSummary {
  /** Number of distinct recommended (team × shift × weekdays) entries drafted. */
  entryCount: number;
  /** Concrete dated occurrences shown on the visible week. */
  occurrenceCount: number;
  /** How many entries were assigned to a specific employee. */
  assignedCount: number;
  /** How many entries are open (unassigned) shifts. */
  openCount: number;
}

export type DraftSchedulePeriod = SchedulePeriod;
